import { apiClient } from '@/core/api/client';
import type {
  AnalyticsResponseEnvelope,
  CoverageData,
  CoverageDrillDownData,
  CoverageDrillDownParams,
  CoverageQueryParams,
  SearchData,
  SearchQueryParams,
  EngagementData,
  ActivityData,
  ActivityQueryParams,
  FAClientsData,
  FAClientsQueryParams,
  SPClientsData,
  SPClientsQueryParams,
  RefreshStatusResponse,
  RefreshTriggerResponse,
  PartnerCompaniesResponse,
  ExportSection,
  AnalyticsQueryParams,
  FpOverviewData,
  FpActionsData,
  FpReferralsByServiceData,
  FpTopSearchesData,
  FpClientActionsData,
  FpEngagementRateTrendData,
  FpTopAdvisorsData,
  FpAdvisorRetentionData,
  FpTrendingCategoriesData,
  FpSearchVolumeData,
  FpRegionalDistributionData,
  FpEmergingRegionsData,
  FpUnmetDemandData,
} from '../types/analytics.types';
import type {
  EngagementMetricsOverviewData,
  EngagementMetricsQueryParams,
  EngagementLogData,
  EngagementMetricsDormantProvidersPage,
  ReferralFeedbackData,
  ReferralFeedbackProviderDetail,
  ReferralFeedbackCsDetail,
  ReferralSubjectFeedback,
  ReferralListPage,
  ReferralListQueryParams,
} from '../types/engagement-metrics.types';

const BASE_PATH = '/admin/analytics';

export const analyticsApi = {
  getCoverage: async (
    params?: CoverageQueryParams,
  ): Promise<AnalyticsResponseEnvelope<CoverageData>> => {
    const { data } = await apiClient.get(`${BASE_PATH}/coverage`, { params });
    return data;
  },

  getCoverageDrillDown: async (
    params: CoverageDrillDownParams,
  ): Promise<AnalyticsResponseEnvelope<CoverageDrillDownData>> => {
    const { data } = await apiClient.get(`${BASE_PATH}/coverage/drill-down`, { params });
    return data;
  },

  getSearch: async (
    params?: SearchQueryParams,
  ): Promise<AnalyticsResponseEnvelope<SearchData>> => {
    const { data } = await apiClient.get(`${BASE_PATH}/search`, { params });
    return data;
  },

  getEngagements: async (
    params?: AnalyticsQueryParams,
  ): Promise<AnalyticsResponseEnvelope<EngagementData>> => {
    const { data } = await apiClient.get(`${BASE_PATH}/engagements`, { params });
    return data;
  },

  getEngagementMetrics: async (
    params?: EngagementMetricsQueryParams,
  ): Promise<AnalyticsResponseEnvelope<EngagementMetricsOverviewData>> => {
    const { data } = await apiClient.get(`${BASE_PATH}/engagement-metrics`, { params });
    return data;
  },

  getDormantProviders: async (
    params?: EngagementMetricsQueryParams,
  ): Promise<{ section: string; data: EngagementMetricsDormantProvidersPage }> => {
    const { data } = await apiClient.get(`${BASE_PATH}/engagement-metrics/dormant-providers`, {
      params,
    });
    return data;
  },

  getEngagementMetricsLog: async (
    params?: EngagementMetricsQueryParams,
  ): Promise<AnalyticsResponseEnvelope<EngagementLogData>> => {
    const { data } = await apiClient.get(`${BASE_PATH}/engagement-metrics/log`, { params });
    return data;
  },

  // Feature 117: Admin Referrals tab.
  getReferralList: async (params?: ReferralListQueryParams): Promise<ReferralListPage> => {
    const { data } = await apiClient.get(`${BASE_PATH}/engagement-metrics/referrals`, { params });
    return data;
  },

  exportReferralsCSV: async (
    params?: Omit<ReferralListQueryParams, 'cursor' | 'limit'>,
  ): Promise<Blob> => {
    const { data } = await apiClient.get(`${BASE_PATH}/engagement-metrics/referrals/export`, {
      params,
      responseType: 'blob',
    });
    return data;
  },

  exportEngagementMetricsCSV: async (
    params?: Omit<EngagementMetricsQueryParams, 'cursor' | 'limit'>,
  ): Promise<Blob> => {
    const { data } = await apiClient.get(`${BASE_PATH}/engagement-metrics/export`, {
      params,
      responseType: 'blob',
    });
    return data;
  },

  // Feature 102 slice 4 — returns { spSummary, csSummary, reasons } directly (no envelope).
  getReferralFeedback: async (): Promise<ReferralFeedbackData> => {
    const { data } = await apiClient.get(`${BASE_PATH}/referral-feedback`);
    return data;
  },

  getReferralFeedbackProviderDetail: async (
    spUserId: string,
  ): Promise<ReferralFeedbackProviderDetail> => {
    const { data } = await apiClient.get(
      `${BASE_PATH}/referral-feedback/provider/${spUserId}`,
    );
    return data;
  },

  // Care-seeker-initiated feedback items for one provider (CS drill-down).
  getReferralFeedbackCsDetail: async (spUserId: string): Promise<ReferralFeedbackCsDetail> => {
    const { data } = await apiClient.get(
      `${BASE_PATH}/referral-feedback/provider/${spUserId}/care-seeker`,
    );
    return data;
  },

  // Feedback for one engagement-log row. Legacy rows may carry composite
  // non-UUID subject ids — encoded here; the endpoint answers misses with nulls.
  getReferralSubjectFeedback: async (
    subjectType: string,
    subjectId: string,
  ): Promise<ReferralSubjectFeedback> => {
    const { data } = await apiClient.get(
      `${BASE_PATH}/referral-feedback/subject/${subjectType}/${encodeURIComponent(subjectId)}`,
    );
    return data;
  },

  getActivityLogins: async (
    params?: ActivityQueryParams,
  ): Promise<AnalyticsResponseEnvelope<ActivityData>> => {
    const { data } = await apiClient.get(`${BASE_PATH}/activity/logins`, { params });
    return data;
  },

  getFAClients: async (
    params?: FAClientsQueryParams,
  ): Promise<AnalyticsResponseEnvelope<FAClientsData>> => {
    const { data } = await apiClient.get(`${BASE_PATH}/activity/fa-clients`, { params });
    return data;
  },

  getSPClients: async (
    params?: SPClientsQueryParams,
  ): Promise<AnalyticsResponseEnvelope<SPClientsData>> => {
    const { data } = await apiClient.get(`${BASE_PATH}/activity/sp-clients`, { params });
    return data;
  },

  getFPOverview: async (
    params?: AnalyticsQueryParams,
  ): Promise<AnalyticsResponseEnvelope<FpOverviewData>> => {
    const { data } = await apiClient.get(`${BASE_PATH}/fp/overview`, { params });
    return data;
  },

  getFPActions: async (
    params?: AnalyticsQueryParams,
  ): Promise<AnalyticsResponseEnvelope<FpActionsData>> => {
    const { data } = await apiClient.get(`${BASE_PATH}/fp/advisor-actions`, { params });
    return data;
  },

  getFPReferralsByService: async (
    params?: AnalyticsQueryParams,
  ): Promise<AnalyticsResponseEnvelope<FpReferralsByServiceData>> => {
    const { data } = await apiClient.get(`${BASE_PATH}/fp/referrals-by-service`, { params });
    return data;
  },

  getFPTopSearches: async (
    params?: AnalyticsQueryParams,
  ): Promise<AnalyticsResponseEnvelope<FpTopSearchesData>> => {
    const { data } = await apiClient.get(`${BASE_PATH}/fp/top-searches`, { params });
    return data;
  },

  getFPClientActions: async (
    params?: AnalyticsQueryParams,
  ): Promise<AnalyticsResponseEnvelope<FpClientActionsData>> => {
    const { data } = await apiClient.get(`${BASE_PATH}/fp/client-actions`, { params });
    return data;
  },

  getFPEngagementRateTrend: async (
    params?: AnalyticsQueryParams,
  ): Promise<AnalyticsResponseEnvelope<FpEngagementRateTrendData>> => {
    const { data } = await apiClient.get(`${BASE_PATH}/fp/engagement-rate-trend`, { params });
    return data;
  },

  getFPTopAdvisors: async (
    params?: AnalyticsQueryParams,
  ): Promise<AnalyticsResponseEnvelope<FpTopAdvisorsData>> => {
    const { data } = await apiClient.get(`${BASE_PATH}/fp/top-advisors`, { params });
    return data;
  },

  getFPAdvisorRetention: async (
    params?: AnalyticsQueryParams,
  ): Promise<AnalyticsResponseEnvelope<FpAdvisorRetentionData>> => {
    const { data } = await apiClient.get(`${BASE_PATH}/fp/advisor-retention`, { params });
    return data;
  },

  getFPTrendingCategories: async (
    params?: AnalyticsQueryParams,
  ): Promise<AnalyticsResponseEnvelope<FpTrendingCategoriesData>> => {
    const { data } = await apiClient.get(`${BASE_PATH}/fp/trending-categories`, { params });
    return data;
  },

  getFPSearchVolume: async (
    params?: AnalyticsQueryParams,
  ): Promise<AnalyticsResponseEnvelope<FpSearchVolumeData>> => {
    const { data } = await apiClient.get(`${BASE_PATH}/fp/search-volume`, { params });
    return data;
  },

  getFPRegionalDistribution: async (
    params?: AnalyticsQueryParams,
  ): Promise<AnalyticsResponseEnvelope<FpRegionalDistributionData>> => {
    const { data } = await apiClient.get(`${BASE_PATH}/fp/regional-distribution`, { params });
    return data;
  },

  getFPEmergingRegions: async (
    params?: AnalyticsQueryParams,
  ): Promise<AnalyticsResponseEnvelope<FpEmergingRegionsData>> => {
    const { data } = await apiClient.get(`${BASE_PATH}/fp/emerging-regions`, { params });
    return data;
  },

  getFPUnmetDemand: async (
    params?: AnalyticsQueryParams,
  ): Promise<AnalyticsResponseEnvelope<FpUnmetDemandData>> => {
    const { data } = await apiClient.get(`${BASE_PATH}/fp/unmet-demand`, { params });
    return data;
  },

  triggerRefresh: async (): Promise<RefreshTriggerResponse> => {
    const { data } = await apiClient.post(`${BASE_PATH}/refresh`);
    return data;
  },

  getRefreshStatus: async (): Promise<RefreshStatusResponse> => {
    const { data } = await apiClient.get(`${BASE_PATH}/refresh/status`);
    return data;
  },

  getPartnerCompanies: async (): Promise<PartnerCompaniesResponse> => {
    const { data } = await apiClient.get(`${BASE_PATH}/partner-companies`);
    return data;
  },

  exportCSV: async (
    section: ExportSection,
    params?: Record<string, string | number | boolean | undefined>,
  ): Promise<Blob> => {
    const { data } = await apiClient.get(`${BASE_PATH}/export/${section}`, {
      params,
      responseType: 'blob',
    });
    return data;
  },
};
